import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react'
import PropTypes from 'prop-types'

/* videojs requires videojs.css, but this cannot load asynchronously for some reason. Therefore, we add it to GlobalStyles */
import videojs from 'video.js'

// Set up chromecast
import Chromecast from '@silvermine/videojs-chromecast'
import '@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css'

import 'videojs-hotkeys/videojs.hotkeys'
import 'videojs-contrib-quality-levels'

// We import these components so that they will register with videojs. See options.controlBar.children below for use.
import '../vjsComponents'

import { getFileExtension, isiPhone, isSafari } from 'helpers'
import { SHOW_SETTINGS_EVENT } from './customEvents'

import reducer, {
  initialState,
  initializeState,
  AUTO_BITRATE,
  INIT_RESOLUTIONS,
  SET_FIRST_PLAY,
  SET_METADATA,
  SET_BITRATE,
  TOGGLE_SETTINGS,
  UPDATE_TIME,
  UPDATE_VIDEO_PLAYER_STATUS,
  TRIAL_EXTEND
} from './reducer'
import { casting_initiated_event } from 'gtm/pushEvents'

require('https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1')

// initialize Chromecast plugin
Chromecast(videojs)

const Store = React.createContext(null)
export const useVideoPlayerContext = () => React.useContext(Store)

const SOURCE_TYPES = {
  m3u8: 'application/x-mpegURL',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/mp4'
}

/*********
 * VideoPlayerContext sets up and manages our video player.
 * It exposes a few stateful objects that children can hook into:
 *
 * @param {string} src - The source of the video.
 * @param {string} poster - The url for the poster to be displayed before playing.
 * @param {number} videoId - Id of the video playing, this will trigger musicPlayer and tracking if present.
 * @param {Object} planMeta - An object with plan meta-information
 * @param {number} planMeta.planId - The id of the plan to which this video belongs
 * @param {number} planMeta.planEntryId - The planEntry id to which this video belongs
 * @param {string} planMeta.videoOriginType - The type of page that belongs the video
 *
 * @returns {Object} state - { currentBitrate, meta, resolutions, showSettings, time }
 * @returns {Object} actions - { setBitrate(), toggleShow() }
 * @returns {Object} videoPlayer - the video player object
 * @returns {Object} videoRef - the useRef() ref used to attach to the video player dom element.
 */
const VideoPlayerContext = ({
  src,
  type,
  poster,
  videoId,
  planMeta,
  resolutions,
  trialExtend,
  children
}) => {
  const videoRef = useRef()
  const [videoPlayer, setVideoPlayer] = useState()
  const throttleTime = useRef(null) // {int} - We use a ref to throttle our event lookup to once per second.
  const [state, dispatch] = useReducer(reducer, initialState, initializeState)

  const toggleSettings = () => {
    dispatch({ type: TOGGLE_SETTINGS })
  }

  const setFirstPlay = () => {
    dispatch({ type: SET_FIRST_PLAY })
  }

  /**
   * Sets a user's target bitrate, updates state, and persists their choice to localStorage.
   * @param {number} bitrate - the target bitrate we wish to switch to.
   */
  const setBitrate = useCallback(
    (bitrate) => {
      const currentBitrate = state.currentBitrate

      if (currentBitrate !== bitrate) {
        window.localStorage.setItem('video-bitrate', bitrate)
        const qualityLevels = videoPlayer.qualityLevels()

        for (let i = 0; i < qualityLevels.length; i++) {
          let level = qualityLevels[i]
          if (level.bitrate === bitrate || bitrate === AUTO_BITRATE) {
            level.enabled = true
          } else {
            level.enabled = false
          }
        }

        dispatch({ type: SET_BITRATE, payload: bitrate })
      }
    },
    [videoPlayer, state]
  )

  /* Set up meta information */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const meta = {
      ...planMeta,
      videoId,
      type: params.get('type'),
      uuid: params.get('uuid'),
      referrer: params.get('referrer') || document.referrer
    }

    dispatch({ type: SET_METADATA, payload: meta })
  }, [planMeta, videoId])

  /* Set up and instantiate video player */
  useEffect(() => {
    if (videoRef.current) {
      // Grab the file extension from the incoming source and map it to a valid source type
      // We allow an explicit type override, because local file blobs do not have extensions.
      const srcType = SOURCE_TYPES[type] || SOURCE_TYPES[getFileExtension(src)]

      // Set up video player options.
      // We use options.controlBar.children, rather than other ways to populate the control bar, so we can have it set tab-stops correctly.
      const options = {
        controls: true,
        bigPlayButton: false,
        controlBar: {
          pictureInPictureToggle: false,
          volumePanel: false,
          children: [
            'seekBackward',
            'playToggle',
            'seekForward',
            'currentTimeDisplay',
            'progressControl',
            'remainingTimeDisplay',
            'fullscreenToggle'
          ]
        },
        sources: {
          src,
          type: srcType
        },
        techOrder: ['chromecast', 'html5'],
        poster
      }

      // we get the bunny token to bring and sign the chunks of this video
      let queryString = ''
      if (src.includes('?') && src.includes('token')) {
        queryString = src.substring(src.indexOf('?'))
      }

      // .ts files are the chunks of the streamming video (aka. file.m3u8)
      // Every .ts file have a lenght of 4 seconds,
      // This function allows us to append the auth-token to the request whenever a chunk file is requested.
      videojs.Vhs.xhr.beforeRequest = function (options) {
        if (/(\.ts|video\.m3u8)$/.test(options.uri)) {
          options.uri += queryString
        }
      }

      const vjsPlayer = videojs(videoRef.current, options)

      // we need to set the button position for Chromecast here, rather than in options.controlBar.children because that's the way this plugin works I guess. ¯\_(ツ)_/¯
      vjsPlayer.chromecast({ buttonPositionIndex: 6 })

      setVideoPlayer(vjsPlayer)
    }

    return () => {
      // clear out player when component is unmounted.
      if (videoPlayer) {
        videoPlayer.dispose()
      }
    }
  }, [poster, src, type, videoPlayer, videoRef])

  /* Attach event listeners to videoPlayer once it is instantiated */
  useEffect(() => {
    if (videoPlayer) {
      // Disable right clicking on the player itself.
      const disableRightClick = () => {
        videoPlayer.contentEl().oncontextmenu = () => {
          return false
        }
      }

      // Set up hotkeys
      const setupHotkeys = () => {
        videoPlayer.hotkeys({
          volumeStep: 0.1,
          seekStep: 15,
          enableMute: true,
          enableFullscreen: true,
          enableVolumeScroll: false
        })
      }
      // A wrapper for all of our on('ready') callbacks
      const onReadyCallbacks = () => {
        disableRightClick()
        setupHotkeys()
      }

      const setStatus = ({ type }) => {
        dispatch({ type: UPDATE_VIDEO_PLAYER_STATUS, payload: type })
      }

      // Keep our state.time in sync to the nearest second.
      const handleTimeChange = () => {
        // handleTimeChange needs to use a throttleTime ref because it is a callback in an event listener.
        // This means that React can't update it via useCallback.
        const currentVideoTime = Math.floor(videoPlayer.currentTime())

        if (throttleTime.current !== currentVideoTime) {
          dispatch({ type: UPDATE_TIME, payload: currentVideoTime })
          throttleTime.current = currentVideoTime // keep throttle time ref updated.
        }
      }

      // Create a resolutions array, sorted highest bitrate to lowest, and add it to state.
      // Our resolution picker uses this state to work.
      const initResolutions = () => {
        if (!isSafari() || isiPhone()) {
          const qualityLevels = videoPlayer.qualityLevels()
          let resolutions = []

          // qualityLevels is not an iterable, but its getter prototype is overloaded to act as an array. ¯\_(ツ)_/¯
          for (let i = 0; i < qualityLevels.length; i++) {
            const bitrate = qualityLevels[i].bitrate
            const resolution = qualityLevels[i].height
            if (resolution && bitrate) {
              resolutions.push({ bitrate, resolution })
              continue
            }
            // Videos on BunnyCDN have a bitrate and resolution in playlist.m3u8, so logic below only applies to AWS.
            // The switch logic remains below in case we switch back to AWS for our streaming CDN (e.g. if BunnyCDN
            // has an outage)
            switch (bitrate) {
              case 5000000: {
                resolutions.push({
                  bitrate,
                  resolution: '720'
                })
                break
              }
              case 2040000: {
                resolutions.push({
                  bitrate,
                  resolution: '480'
                })
                break
              }
              case 640000: {
                resolutions.push({
                  bitrate,
                  resolution: '360'
                })
                break
              }
              default: {
                break
              }
            }
          }

          // If there are resolutions present, then we enable settings menu.
          if (resolutions.length > 0) {
            // We can't ensure the order of our resolutions, so we sort them highest to lowest here.
            resolutions.sort((a, b) => b.resolution - a.resolution)
            videoPlayer.controlBar.addChild('settings')
          }

          dispatch({ type: INIT_RESOLUTIONS, payload: resolutions })
        }
      }

      videoPlayer.qualityLevels().on('addqualitylevel', (e) => {
        const qualityLevel = e.qualityLevel
        // We do not need, or want, to add state to our dep array, as:
        // 1) We do not want to trigger the effect on every state change.
        // 2) We only need the initial value in state.
        if (
          state.currentBitrate === qualityLevel.bitrate ||
          state.currentBitrate === AUTO_BITRATE
        ) {
          qualityLevel.enabled = true
        } else {
          qualityLevel.enabled = false
        }
      })
      const onTrialExtend = () =>
        dispatch({ type: TRIAL_EXTEND, payload: trialExtend })

      videoPlayer.on('ready', onTrialExtend)
      videoPlayer.on('ready', onReadyCallbacks)
      videoPlayer.on('pause', setStatus)
      videoPlayer.on('play', setStatus)
      videoPlayer.on('chromecastConnected', () => {
        // When chromecast is going, trigger casting_initiated event
        casting_initiated_event('chromecast')
      })
      videoPlayer.on('airplayConnected', () => {
        // When airplay is going, trigger casting_initiated event
        casting_initiated_event('airplay')
      })
      videoPlayer.on('loadedmetadata', initResolutions)
      videoPlayer.on('timeupdate', handleTimeChange)
      videoPlayer.on(SHOW_SETTINGS_EVENT, toggleSettings)
      videoPlayer.one('play', setFirstPlay)
    }

    return () => {
      if (videoPlayer) {
        return videoPlayer.dispose()
      }
    }
  }, [videoPlayer])

  const actions = {
    setBitrate,
    toggleSettings
  }

  return (
    <Store.Provider
      value={{ state, actions, resolutions, videoPlayer, videoRef }}
    >
      {children}
    </Store.Provider>
  )
}

VideoPlayerContext.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  videoId: PropTypes.number,
  planMeta: PropTypes.shape({
    planId: PropTypes.number,
    planEntryId: PropTypes.number,
    videoOriginType: PropTypes.string
  }),
  children: PropTypes.node
}

export default VideoPlayerContext
